/**
 * Dice Registration Page - Wizard for registering custom dice
 *
 * Steps:
 * 1. Select dice type and optional name
 * 2. Capture each face
 * 3. Review captured faces
 * 4. Training status
 */

import {
  DiceType,
  CaptureMode,
  IMAGE_REQUIREMENTS
} from '@dice-capture/shared/types/dice-registry';
import { ArrowLeft, Crop, Trash2 } from 'lucide-react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { CameraView, CameraViewHandle, QualityAssessment, MotionState, TorchButton, RefocusButton, type TorchState, type RefocusState } from '@/components/CameraView';
import { CameraTopBar } from '@/components/CameraTopBar';
import { QualityDot, LOW_QUALITY_THRESHOLD } from '@/components/QualityDot';
import { FrogMascot } from '@/components/FrogMascot';
import { LoadingFrog } from '@/components/LoadingFrog';
import { FloatingCaptureButton, CaptureSettingsDrawer } from '@/components/registration';
import { ImageCropper } from '@/components/registration/ImageCropper';
import { RotationOverlay } from '@/components/RotationOverlay';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter
} from '@/components/ui/drawer';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { VideoCaptureWizard } from '@/components/VideoCaptureWizard';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { cn } from '@/lib/utils';
import { useDiceRegistryStore, type FaceImage } from '@/stores/diceRegistryStore';

const SERVICE_TAG = '[DICE_REG_PAGE]';

/** Available dice types for registration */
const DICE_TYPES: DiceType[] = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20'];

/**
 * Props for TrainingIntensitySelector
 */
interface TrainingIntensitySelectorProps {
  /** Callback when mode is selected and user clicks continue */
  onContinue: () => void;
}

/**
 * Training intensity selection - choose between recommended and light modes
 */
function TrainingIntensitySelector({ onContinue }: TrainingIntensitySelectorProps) {
  const { trainingIntensity, setTrainingIntensity } = useDiceRegistryStore();

  // Move to dice type selection when intensity is confirmed
  const handleContinue = () => {
    console.info(`${SERVICE_TAG} Training intensity confirmed`, { trainingIntensity });
    onContinue();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Choose Training Mode</h2>
        <p className="text-muted-foreground">
          How thoroughly do you want to train?
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Card
          data-testid="mode-recommended"
          onClick={() => setTrainingIntensity('recommended')}
          className={cn(
            'cursor-pointer transition-all hover:border-primary/50',
            trainingIntensity === 'recommended' && 'border-primary border-2'
          )}
        >
          <CardHeader>
            <CardTitle className="text-lg">Recommended</CardTitle>
            <CardDescription>
              Best accuracy. 30-50 images per face.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card
          data-testid="mode-light"
          onClick={() => setTrainingIntensity('light')}
          className={cn(
            'cursor-pointer transition-all hover:border-primary/50',
            trainingIntensity === 'light' && 'border-primary border-2'
          )}
        >
          <CardHeader>
            <CardTitle className="text-lg">Light</CardTitle>
            <CardDescription>
              Faster setup. 30-40 images total across all faces.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card
          data-testid="mode-unlimited"
          onClick={() => setTrainingIntensity('unlimited')}
          className={cn(
            'cursor-pointer transition-all hover:border-primary/50',
            trainingIntensity === 'unlimited' && 'border-primary border-2'
          )}
        >
          <CardHeader>
            <CardTitle className="text-lg">No Limit</CardTitle>
            <CardDescription>
              Capture freely. Any angle, any background, as many photos as you want.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      <Button
        className="w-full"
        variant="accent"
        size="lg"
        onClick={handleContinue}
        data-testid="continue-to-dice-type"
      >
        Continue
      </Button>
    </div>
  );
}

/**
 * Dice type selection grid
 */
function DiceTypeSelector({ onBack }: { onBack?: () => void }) {
  const { currentRegistration, setDiceType, setDiceName, startRegistration, isLoading, error } =
    useDiceRegistryStore();

  const handleStartRegistration = () => {
    console.info(`${SERVICE_TAG} Starting registration flow`, {
      diceType: currentRegistration.diceType,
      diceName: currentRegistration.diceName
    });
    startRegistration();
  };

  return (
    <div className="space-y-4">
      {onBack && (
        <Button variant="ghost" size="sm" className="self-start gap-1" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      )}
      <div className="text-center flex flex-col items-center">
        <FrogMascot variant="thinking" size="md" className="mb-3" />
        <h2 className="text-2xl font-bold mb-2">Register Your Dice</h2>
        <p className="text-muted-foreground">
          Train a custom model to recognize your specific dice
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {DICE_TYPES.map((type) => (
          <Button
            key={type}
            variant={currentRegistration.diceType === type ? 'accent' : 'secondary-action'}
            className="h-20 text-xl font-bold"
            onClick={() => setDiceType(type)}
          >
            {type.toUpperCase()}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Dice Name (optional)</label>
        <Input
          placeholder="e.g., Blue Sparkle d20"
          value={currentRegistration.diceName}
          onChange={(e) => setDiceName(e.target.value)}
          disabled={!currentRegistration.diceType}
        />
      </div>

      {error && (
        <div
          id="registration-error"
          className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </div>
      )}

      <Button
        className="w-full"
        variant="accent"
        size="lg"
        disabled={!currentRegistration.diceType || isLoading}
        onClick={handleStartRegistration}
        aria-describedby={error ? 'registration-error' : undefined}
      >
        {isLoading ? 'Starting...' : 'Start Registration'}
      </Button>
    </div>
  );
}

/** Interval in ms for live quality checks */
const QUALITY_CHECK_INTERVAL_MS = 500;

/**
 * Props for CaptureModeToggle component
 */
interface CaptureModeToggleProps {
  /** Current capture mode */
  mode: CaptureMode;
  /** Callback when mode changes */
  onChange: (mode: CaptureMode) => void;
}

/**
 * Toggle component for switching between manual and video capture modes
 */
function CaptureModeToggle({ mode, onChange }: CaptureModeToggleProps) {
  return (
    <div
      className="flex rounded-lg bg-secondary p-1 mb-4"
      role="tablist"
      aria-label="Capture mode selection"
    >
      <button
        role="tab"
        aria-selected={mode === 'manual'}
        className={cn(
          'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all',
          mode === 'manual'
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
        onClick={() => onChange('manual')}
      >
        Manual
      </button>
      <button
        role="tab"
        aria-selected={mode === 'video'}
        className={cn(
          'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all',
          mode === 'video'
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
        onClick={() => onChange('video')}
      >
        Video
      </button>
    </div>
  );
}

/**
 * Face capture view with camera and quality gates
 * Supports both manual face-by-face and video capture modes
 */
function FaceCaptureView() {
  const isLandscape = useIsLandscape();
  const cameraRef = useRef<CameraViewHandle>(null);

  // Use selectors with shallow equality to ensure re-renders on nested state changes
  const {
    currentFace,
    totalFaces,
    diceType,
    captureMode,
    currentRotation,
    degreesPerCapture,
    capturesPerFace,
    faces,
    faceProgressionPending
  } = useDiceRegistryStore(
    useShallow((state) => ({
      currentFace: state.currentRegistration.currentFace,
      totalFaces: state.currentRegistration.totalFaces,
      diceType: state.currentRegistration.diceType,
      captureMode: state.currentRegistration.captureMode,
      currentRotation: state.currentRegistration.currentRotation,
      degreesPerCapture: state.currentRegistration.degreesPerCapture,
      capturesPerFace: state.currentRegistration.capturesPerFace,
      faces: state.currentRegistration.faces,
      faceProgressionPending: state.currentRegistration.faceProgressionPending
    }))
  );

  // Extract actions and other top-level state separately (stable references)
  const {
    addImageToFace,
    advanceRotation,
    removeImageFromFace,
    replaceImageOnFace,
    setCaptureMode,
    goToFaceSelection,
    isLoading,
    autoCaptureEnabled,
    setAutoCaptureEnabled,
    setDegreesPerCapture,
    trainingIntensity,
    confirmFaceProgression,
    dismissFaceProgression
  } = useDiceRegistryStore();

  // Get images for the current face
  const currentFaceImages = faces.find(f => f.faceNumber === currentFace)?.images ?? [];

  // TICKET-073: Calculate minimum images per face based on training intensity
  // TICKET-180: 'unlimited' mode has no minimum — use 1 (backend floor)
  const minImagesPerFace = diceType && trainingIntensity !== 'unlimited'
    ? Math.ceil(IMAGE_REQUIREMENTS[trainingIntensity][diceType].min / totalFaces)
    : 1;
  const hasMinimumImages = currentFaceImages.length >= minImagesPerFace;
  const isLastFace = currentFace === totalFaces;

  // Live quality preview state
  const [liveQuality, setLiveQuality] = useState<QualityAssessment | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  // Last capture quality (shown after capture attempt)
  const [captureQuality, setCaptureQuality] = useState<QualityAssessment | null>(null);
  const [showCaptureQuality, setShowCaptureQuality] = useState(false);

  // Quality gate error message
  const [qualityError, setQualityError] = useState<string | null>(null);

  // Rotation overlay visibility state
  // TICKET-180: Hidden by default in 'unlimited' mode (no rotation guidance enforced)
  const [overlayVisible, setOverlayVisible] = useState(trainingIntensity !== 'unlimited');

  // TICKET-071: Auto-capture motion detection state
  const [motionState, setMotionState] = useState<MotionState>('detecting');
  const [stabilityProgress, setStabilityProgress] = useState(0);

  // TICKET-104: Drawer state for settings
  const [drawerOpen, setDrawerOpen] = useState(false);
  // TICKET-104: Auto-capture pause state (local, resets on unmount)
  const [autoCapturePaused, setAutoCapturePaused] = useState(false);

  // Camera button state (pushed from CameraView via callbacks)
  const [torchState, setTorchState] = useState<TorchState>({ supported: false, enabled: false });
  const [refocusState, setRefocusState] = useState<RefocusState>({ isRefocusing: false });

  // TICKET-116: Swipe gesture support for drawer handle
  const swipeDirection = isLandscape ? 'left' : 'up';
  const { wasSwipe, ...swipeHandlers } = useSwipeGesture({
    direction: swipeDirection,
    threshold: 30, // Reduced threshold for small touch target (handle is only 24px)
    maxDuration: 500,
    onSwipe: () => {
      console.info(`${SERVICE_TAG} Drawer opened via swipe gesture`, {
        direction: swipeDirection
      });
      setDrawerOpen(true);
    }
  });

  // Ref to hold latest capture function (avoids stale closure in motion detection callback)
  const performCaptureRef = useRef<() => Promise<void>>(() => Promise.resolve());

  /**
   * Check quality at regular intervals when camera is active
   */
  useEffect(() => {
    if (!cameraReady) {
      return;
    }

    const checkQuality = () => {
      const quality = cameraRef.current?.assessQuality();
      if (quality) {
        setLiveQuality(quality);
      }
    };

    // Initial check
    checkQuality();

    // Set up interval for live updates
    const intervalId = setInterval(checkQuality, QUALITY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [cameraReady]);

  /**
   * TICKET-071: Start/stop motion detection based on auto-capture setting
   * TICKET-104: Respect pause state - motion detection continues but capture is skipped
   */
  useEffect(() => {
    const camera = cameraRef.current;

    if (!cameraReady || !autoCaptureEnabled || captureMode !== 'manual') {
      // Stop motion detection if not ready, disabled, or in video mode
      camera?.stopMotionDetection();
      setMotionState('detecting');
      setStabilityProgress(0);
      return;
    }

    console.info(`${SERVICE_TAG} Starting auto-capture motion detection`);

    camera?.startMotionDetection({
      stabilityDurationMs: 1500, // 1.5 seconds of stability before capture
      onStateChange: (state, progress) => {
        setMotionState(state);
        setStabilityProgress(progress);
      },
      onSettled: async () => {
        // Skip capture if paused or progression prompt is showing
        if (autoCapturePaused) {
          console.info(`${SERVICE_TAG} Auto-capture paused, skipping capture`);
          camera?.resetMotionSettled();
          return;
        }

        // TICKET-095: Get latest faceProgressionPending state
        const { currentRegistration } = useDiceRegistryStore.getState();
        if (currentRegistration.faceProgressionPending) {
          console.info(`${SERVICE_TAG} Face progression pending, skipping auto-capture`);
          camera?.resetMotionSettled();
          return;
        }

        // Auto-capture when settled (use ref to get latest capture function)
        console.info(`${SERVICE_TAG} Auto-capture triggered - scene settled`);
        camera?.resetMotionSettled();
        try {
          await performCaptureRef.current();
          console.info(`${SERVICE_TAG} Auto-capture completed successfully`);
        } catch (error) {
          console.error(`${SERVICE_TAG} Auto-capture failed:`, {
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }
    });

    return () => {
      camera?.stopMotionDetection();
    };
  }, [cameraReady, autoCaptureEnabled, captureMode, currentFace, autoCapturePaused]);

  /**
   * Handle camera ready event
   */
  const handleCameraReady = useCallback(() => {
    console.info(`${SERVICE_TAG} Camera ready for quality assessment`);
    setCameraReady(true);
  }, []);

  /**
   * Perform the actual capture (shared by manual and auto-capture)
   */
  const performCapture = useCallback(async () => {
    setShowCaptureQuality(false);
    setQualityError(null);

    // Check quality before capture
    const preQuality = cameraRef.current?.assessQuality();

    if (preQuality && !preQuality.isAcceptable) {
      // Quality gate blocks capture
      const primaryReason = preQuality.rejectionReasons[0] ?? 'Image quality too low';
      setQualityError(primaryReason);
      setCaptureQuality(preQuality);
      setShowCaptureQuality(true);

      console.warn(`${SERVICE_TAG} Quality gate blocked capture`, {
        faceNumber: currentFace,
        isAcceptable: preQuality.isAcceptable,
        rejectionReasons: preQuality.rejectionReasons,
        overallScore: preQuality.overallScore.toFixed(2)
      });

      // Reset motion detection to try again
      if (autoCaptureEnabled) {
        cameraRef.current?.resetMotionSettled();
      }

      return;
    }

    // Use multi-frame capture for better quality selection
    const result = await cameraRef.current?.captureMultiple({
      frameCount: 3,
      intervalMs: 150
    });

    if (result) {
      console.info(`${SERVICE_TAG} Multi-frame capture completed`, {
        bestQuality: result.qualityScores[result.allFrames.indexOf(result.bestFrame)] ?? result.qualityScores[0],
        allScores: result.qualityScores.map((s) => s.toFixed(2))
      });

      // Find the quality score for the best frame
      const bestFrameIndex = result.allFrames.indexOf(result.bestFrame);
      const qualityScore = bestFrameIndex >= 0 ? result.qualityScores[bestFrameIndex] : result.qualityScores[0];

      // Create a quality assessment for display (using live quality as base)
      const displayQuality: QualityAssessment = liveQuality ?? {
        isAcceptable: true,
        blur: { score: 200, acceptable: true },
        brightness: { value: 128, acceptable: true },
        contrast: { stdDev: 50, acceptable: true },
        glare: { percentage: 0, hasGlare: false },
        overallScore: qualityScore,
        rejectionReasons: [],
        warnings: []
      };

      setCaptureQuality({ ...displayQuality, overallScore: qualityScore });
      setShowCaptureQuality(true);

      // Log warning if quality is low
      if (qualityScore < LOW_QUALITY_THRESHOLD) {
        console.warn(`${SERVICE_TAG} Low quality capture detected`, {
          qualityScore,
          threshold: LOW_QUALITY_THRESHOLD,
          faceNumber: currentFace
        });
      }

      // Convert base64 to blob (strip the data URL prefix if present)
      const base64Data = result.bestFrame.replace(/^data:image\/\w+;base64,/, '');
      const response = await fetch(`data:image/jpeg;base64,${base64Data}`);
      const blob = await response.blob();

      // Add image to current face and upload to S3 (stays on same face for multi-image capture)
      await addImageToFace(currentFace, blob);

      // Advance rotation position to guide next capture angle
      advanceRotation();

      // Reset motion detection to allow capturing another image
      if (autoCaptureEnabled) {
        cameraRef.current?.resetMotionSettled();
      }
    }
  }, [currentFace, currentRotation, autoCaptureEnabled, liveQuality, addImageToFace, advanceRotation]);

  // Keep ref updated with latest performCapture (for motion detection callback)
  useEffect(() => {
    performCaptureRef.current = performCapture;
  }, [performCapture]);

  /**
   * Handle capture with quality gate (manual button press)
   */
  const handleCapture = async () => {
    await performCapture();
  };

  // Reset quality indicators when face changes
  useEffect(() => {
    setShowCaptureQuality(false);
    setCaptureQuality(null);
    setQualityError(null);
  }, [currentFace]);

  // TICKET-104: Intercept browser back button to go to face selection
  useEffect(() => {
    const handlePopState = () => {
      console.info(`${SERVICE_TAG} Back button pressed, navigating to face selection`);
      goToFaceSelection();
      // Push state back to prevent actual navigation
      window.history.pushState(null, '', window.location.href);
    };

    // Push initial state so we can intercept back
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [goToFaceSelection]);

  // Determine if capture button should be disabled
  const isCaptureDisabled = isLoading || (liveQuality !== null && !liveQuality.isAcceptable);

  /**
   * Handle mode change
   */
  const handleModeChange = useCallback((mode: CaptureMode) => {
    console.info(`${SERVICE_TAG} Changing capture mode`, { from: captureMode, to: mode });
    setCaptureMode(mode);
  }, [captureMode, setCaptureMode]);

  /**
   * Handle video capture complete
   */
  const handleVideoComplete = useCallback(() => {
    console.info(`${SERVICE_TAG} Video capture complete, advancing to review`);
    // TODO: Transition to reviewing step when video capture is complete
    // For now, switch back to manual mode for review
    setCaptureMode('manual');
  }, [setCaptureMode]);

  /**
   * Handle image deletion from gallery
   * TICKET-105: Awaits S3 deletion to ensure state stays in sync
   */
  const handleDeleteImage = useCallback(async (imageId: string) => {
    console.info(`${SERVICE_TAG} Deleting image from face ${currentFace}`, { imageId });
    await removeImageFromFace(currentFace, imageId);
  }, [currentFace, removeImageFromFace]);

  // --- Lightbox / cropper state (rendered at page level to avoid drawer stacking context) ---
  const [lightboxImage, setLightboxImage] = useState<FaceImage | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  /** Open lightbox for a captured face image */
  const handleImageClick = useCallback((image: FaceImage) => {
    setDrawerOpen(false);
    setLightboxImage(image);
  }, []);

  /** Close lightbox and reset crop state */
  const handleCloseLightbox = useCallback(() => {
    setLightboxImage(null);
    setIsCropping(false);
    setDrawerOpen(true);
  }, []);

  /** Delete image from lightbox — close lightbox then delete */
  const handleLightboxDelete = useCallback(async () => {
    if (!lightboxImage) return;
    const imageId = lightboxImage.id;
    console.info(`${SERVICE_TAG} Deleting image from lightbox`, { imageId, faceNumber: currentFace, hasS3Key: !!lightboxImage.s3Key });
    setLightboxImage(null);
    setIsCropping(false);
    setDrawerOpen(true);
    const toastId = toast.loading('Deleting image...');
    try {
      await removeImageFromFace(currentFace, imageId);
      // Store sets error state on failure (doesn't throw)
      const storeError = useDiceRegistryStore.getState().error;
      if (storeError) {
        toast.error(storeError, { id: toastId });
      } else {
        toast.success('Image deleted', { id: toastId });
      }
    } catch (error) {
      console.error(`${SERVICE_TAG} Image deletion failed`, { imageId, error: error instanceof Error ? error.message : error });
      toast.error('Failed to delete image', { id: toastId });
    }
  }, [lightboxImage, currentFace, removeImageFromFace]);

  /** Handle crop completion — replace image and close cropper */
  const handleCropComplete = useCallback(async (blob: Blob) => {
    if (!lightboxImage) return;
    console.info(`${SERVICE_TAG} Cropping image`, { imageId: lightboxImage.id, faceNumber: currentFace, newBlobSize: blob.size });
    setIsCropping(false);
    setLightboxImage(null);
    setDrawerOpen(true);
    const toastId = toast.loading('Saving cropped image...');
    try {
      await replaceImageOnFace(currentFace, lightboxImage.id, blob);
      toast.success('Image cropped', { id: toastId });
    } catch (error) {
      console.error(`${SERVICE_TAG} Image crop failed`, { imageId: lightboxImage.id, error: error instanceof Error ? error.message : error });
      toast.error('Failed to save cropped image', { id: toastId });
    }
  }, [lightboxImage, currentFace, replaceImageOnFace]);

  // Render video capture mode
  if (captureMode === 'video') {
    return (
      <div className="space-y-4">
        {/* Mode toggle */}
        <CaptureModeToggle mode={captureMode} onChange={handleModeChange} />

        {/* Video capture wizard */}
        <VideoCaptureWizard
          onSwitchToManual={() => handleModeChange('manual')}
          onComplete={handleVideoComplete}
        />
      </div>
    );
  }

  // TICKET-104: Camera-First Layout with floating capture button and slide-out drawer
  return (
    <div className="relative h-full w-full">
      {/* Camera takes full container */}
      <div className="absolute inset-0 rounded-lg overflow-hidden">
        <CameraView
          ref={cameraRef}
          className="h-full w-full"
          onReady={handleCameraReady}
          onTorchStateChange={setTorchState}
          onRefocusStateChange={setRefocusState}
        />
      </div>

      {/* Unified top bar for camera controls */}
      <CameraTopBar
        left={
          <div className="flex items-center gap-2">
            <button
              onClick={goToFaceSelection}
              className="p-2 rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
              aria-label="Back to review"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <RefocusButton
              onRefocus={() => cameraRef.current?.refocus()}
              isRefocusing={refocusState.isRefocusing}
            />
            {torchState.supported && (
              <TorchButton
                enabled={torchState.enabled}
                onToggle={() => cameraRef.current?.toggleTorch()}
              />
            )}
            <QualityDot
              quality={showCaptureQuality ? captureQuality : liveQuality}
              show={cameraReady}
              isLive={!showCaptureQuality}
            />
          </div>
        }
        center={
          qualityError ? (
            <div
              className="px-3 py-1.5 rounded-md bg-destructive/90 text-destructive-foreground text-sm backdrop-blur-sm max-w-[90vw] text-center"
              role="alert"
              aria-live="assertive"
              data-testid="quality-gate-error"
            >
              {qualityError}
            </div>
          ) : null
        }
        centerSecondary={
          <>
            {autoCaptureEnabled && motionState !== 'detecting' && !autoCapturePaused && (
              <div className={cn(
                'px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm',
                motionState === 'stable' && 'bg-green-500/80 text-white',
                motionState === 'stabilizing' && 'bg-amber-500/80 text-black',
                motionState === 'moving' && 'bg-blue-500/80 text-white'
              )}>
                {motionState === 'stable' && 'Capturing...'}
                {motionState === 'stabilizing' && `Hold steady ${Math.round(stabilityProgress * 100)}%`}
                {motionState === 'moving' && 'Position die...'}
              </div>
            )}
            {autoCaptureEnabled && autoCapturePaused && (
              <div className="px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm bg-gray-500/80 text-white">
                Paused
              </div>
            )}
          </>
        }
      />

      {/* Overlay controls that span the full camera area */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Rotation overlay guide */}
        <div className="pointer-events-auto">
          <RotationOverlay
            isVisible={overlayVisible}
            onToggle={() => setOverlayVisible(!overlayVisible)}
            currentPosition={currentRotation}
            totalPositions={capturesPerFace}
            currentFace={currentFace}
            totalFaces={totalFaces}
            diceType={diceType}
          />
        </div>

      </div>

      {/* Floating capture button - centered bottom */}
      <div className={cn(
        'absolute left-1/2 -translate-x-1/2 z-10',
        isLandscape ? 'bottom-4' : 'bottom-16'
      )}>
        <FloatingCaptureButton
          mode={autoCaptureEnabled ? 'auto' : 'manual'}
          autoState={autoCapturePaused ? 'paused' : 'running'}
          onCapture={handleCapture}
          onEnterAutoMode={() => {
            console.info(`${SERVICE_TAG} Entering auto-capture mode via long press`);
            setAutoCaptureEnabled(true);
            setAutoCapturePaused(false);
          }}
          onExitAutoMode={() => {
            console.info(`${SERVICE_TAG} Exiting auto-capture mode via long press`);
            setAutoCaptureEnabled(false);
            setAutoCapturePaused(false);
          }}
          onPause={() => {
            console.info(`${SERVICE_TAG} Pausing auto-capture`);
            setAutoCapturePaused(true);
          }}
          onResume={() => {
            console.info(`${SERVICE_TAG} Resuming auto-capture`);
            setAutoCapturePaused(false);
          }}
          disabled={isCaptureDisabled}
        />
      </div>

      {/* Settings drawer - slides from bottom (portrait) or right (landscape) */}
      <CaptureSettingsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        currentFace={currentFace}
        totalFaces={totalFaces}
        currentFaceImages={currentFaceImages}
        minImagesPerFace={minImagesPerFace}
        degreesPerCapture={degreesPerCapture}
        onDegreesChange={setDegreesPerCapture}
        onSelectFace={goToFaceSelection}
        onDeleteImage={handleDeleteImage}
        onImageClick={handleImageClick}
        onAddMoreImages={handleCapture}
        hasMinimumImages={hasMinimumImages}
        isLastFace={isLastFace}
        isLoading={isLoading}
        isUnlimitedMode={trainingIntensity === 'unlimited'}
      />

      {/* Drawer handle trigger - click or swipe to open */}
      <button
        type="button"
        onClick={() => {
          // Skip click if this was a swipe gesture (prevents double-firing)
          if (wasSwipe()) {
            return;
          }
          setDrawerOpen(true);
        }}
        {...swipeHandlers}
        className={cn(
          'absolute z-10 flex items-center justify-center touch-none',
          'bg-card/80 backdrop-blur-sm border border-primary/30 rounded-t-lg',
          'transition-colors hover:bg-card',
          'animate-attention-pulse',
          isLandscape
            ? 'right-0 top-1/2 -translate-y-1/2 w-6 h-16 rounded-l-lg rounded-tr-none'
            : 'bottom-0 left-1/2 -translate-x-1/2 w-32 h-6'
        )}
        aria-label="Open capture settings. Click or swipe to open."
      >
        <div className={cn(
          'bg-muted rounded-full',
          isLandscape ? 'w-1 h-8' : 'w-12 h-1'
        )} />
      </button>

      {/* TICKET-095: Face progression confirmation drawer */}
      <Drawer
        open={faceProgressionPending}
        direction={isLandscape ? 'right' : 'bottom'}
        onOpenChange={(open) => {
          if (!open) {
            // User closed drawer without choosing - treat as dismiss
            dismissFaceProgression();
          }
        }}
      >
        <DrawerContent direction={isLandscape ? 'right' : 'bottom'}>
          <DrawerHeader>
            <DrawerTitle className="text-glow-subtle">
              Face {currentFace} Complete
            </DrawerTitle>
            <DrawerDescription>
              You have captured enough images for this face.
              Would you like to add more photos or continue to the next face?
            </DrawerDescription>
          </DrawerHeader>
          <DrawerFooter className="flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                console.info(`${SERVICE_TAG} User chose to add more photos`, {
                  currentFace,
                  imageCount: currentFaceImages.length
                });
                dismissFaceProgression();
              }}
            >
              Add More Photos
            </Button>
            <Button
              variant="accent"
              className="flex-1"
              onClick={() => {
                console.info(`${SERVICE_TAG} User chose to continue to next face`, {
                  currentFace,
                  nextFace: currentFace < totalFaces ? currentFace + 1 : 'review',
                  imageCount: currentFaceImages.length
                });
                confirmFaceProgression();
              }}
            >
              {currentFace === totalFaces ? 'Review All Faces' : `Continue to Face ${currentFace + 1}`}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Image lightbox — rendered at page level to avoid drawer stacking context */}
      <ImageLightbox
        imageUrl={lightboxImage?.thumbnailUrl ?? ''}
        alt={`Face ${currentFace} captured image`}
        open={!!lightboxImage}
        onClose={handleCloseLightbox}
        actions={
          lightboxImage && !isCropping ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsCropping(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
                aria-label="Crop this image"
              >
                <Crop className="w-4 h-4" />
                <span className="text-sm font-medium">Crop</span>
              </button>
              <button
                type="button"
                onClick={handleLightboxDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                aria-label="Delete this image"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm font-medium">Delete</span>
              </button>
            </div>
          ) : undefined
        }
      />

      {/* Image cropper — rendered at page level */}
      {isCropping && lightboxImage && (
        <ImageCropper
          imageUrl={lightboxImage.thumbnailUrl}
          onCropComplete={handleCropComplete}
          onCancel={() => setIsCropping(false)}
        />
      )}
    </div>
  );
}

/**
 * Props for FaceReviewGrid
 */
interface FaceReviewGridProps {
  /** Whether we're in edit mode (TICKET-088) */
  isEditMode?: boolean;
  /** Callback to exit edit mode */
  onExitEdit?: () => void;
}

/**
 * Review grid of captured faces
 */
function FaceReviewGrid({ isEditMode = false, onExitEdit }: FaceReviewGridProps) {
  const { currentRegistration, retakeFace, completeRegistration, resetCurrentRegistration, isLoading, error } =
    useDiceRegistryStore();
  const { faces, totalFaces, currentFace } = currentRegistration;

  // Check if all faces have at least one image
  const allCaptured = faces.every((f) => f.images.length > 0);
  const capturedCount = faces.filter((f) => f.images.length > 0).length;
  const totalImages = faces.reduce((sum, f) => sum + f.images.length, 0);

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-xl font-bold mb-1">
          {isEditMode ? 'Edit Your Dice' : 'Review Your Captures'}
        </h2>
        <p className="text-muted-foreground text-sm">
          Tap any face to add or remove images
        </p>
      </div>

      {/* Face grid with image count badges */}
      <div
        className="grid grid-cols-4 gap-2"
        role="group"
        aria-label="Captured faces grid"
      >
        {faces.map((face) => {
          const imageCount = face.images.length;
          const hasImages = imageCount > 0;

          return (
            <button
              key={face.faceNumber}
              onClick={() => retakeFace(face.faceNumber)}
              aria-label={`Face ${face.faceNumber} - ${hasImages ? `${imageCount} images, click to add more` : 'no images, click to capture'}`}
              className={cn(
                'aspect-square rounded-md flex flex-col items-center justify-center transition-all relative',
                hasImages
                  ? 'bg-green-500/20 border border-green-500/50 text-green-500'
                  : 'bg-secondary/50 border border-border text-muted-foreground'
              )}
            >
              <span className="text-lg font-bold">{face.faceNumber}</span>
              {/* Image count badge */}
              {hasImages && (
                <span className="text-xs opacity-80">{imageCount} img</span>
              )}
            </button>
          );
        })}
      </div>

      <div
        className="text-center text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        {allCaptured
          ? `All ${totalFaces} faces captured! (${totalImages} total images)`
          : `${capturedCount} of ${totalFaces} faces captured (${totalImages} images)`}
      </div>

      {error && (
        <div
          id="review-error"
          className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </div>
      )}

      {/* TICKET-088: Different buttons for edit mode vs new registration */}
      {isEditMode ? (
        <div className="space-y-2">
          <Button
            className="w-full"
            variant="accent"
            size="lg"
            disabled={!allCaptured || isLoading}
            onClick={completeRegistration}
            aria-describedby={error ? 'review-error' : undefined}
          >
            {isLoading ? 'Processing...' : 'Save & Re-train'}
          </Button>
          <Button
            className="w-full"
            variant="outline"
            size="lg"
            onClick={onExitEdit}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            className="w-full"
            variant="accent"
            size="lg"
            disabled={!allCaptured || isLoading}
            onClick={completeRegistration}
            aria-describedby={error ? 'review-error' : undefined}
          >
            {isLoading ? 'Processing...' : 'Complete Registration'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full gap-1"
            onClick={() => retakeFace(currentFace)}
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Capture
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={resetCurrentRegistration}
            disabled={isLoading}
          >
            Start Over
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Training status view
 */
function TrainingStatus() {
  const { currentRegistration, trainingProgress, resetCurrentRegistration } = useDiceRegistryStore();
  const { diceType, diceName, registrationId } = currentRegistration;

  // Subscribe to training status updates when component mounts
  const { subscribeToTrainingStatus, unsubscribeFromTrainingStatus } = useDiceRegistryStore();

  useEffect(() => {
    if (registrationId) {
      subscribeToTrainingStatus(registrationId);
    }
    return () => {
      unsubscribeFromTrainingStatus();
    };
  }, [registrationId, subscribeToTrainingStatus, unsubscribeFromTrainingStatus]);

  // Determine status text based on training progress
  const getStatusText = () => {
    if (!trainingProgress) {
      return 'Training queued...';
    }
    switch (trainingProgress.status) {
      case 'started':
        return 'Training started...';
      case 'progress':
        return `Training in progress: ${trainingProgress.progress}%`;
      case 'complete':
        return 'Training complete!';
      case 'failed':
        return `Training failed: ${trainingProgress.error ?? 'Unknown error'}`;
      default:
        console.warn(`${SERVICE_TAG} Unknown training status, using fallback`, {
          status: trainingProgress.status,
          fallbackText: 'Training in progress...'
        });
        return 'Training in progress...';
    }
  };

  return (
    <div className="space-y-6 text-center" role="region" aria-label="Training status">
      <div
        className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center"
        role="img"
        aria-label="Training indicator"
      >
        <div className="w-12 h-12 rounded-full bg-primary animate-pulse" />
      </div>

      <div>
        <h2 className="text-xl font-bold mb-2">Training in Progress</h2>
        <p className="text-muted-foreground">
          Your {diceName || diceType} is being trained.
          <br />
          This typically takes 2-4 hours.
        </p>
      </div>

      {/* Training progress indicator */}
      <div
        className="text-sm font-medium text-primary"
        role="status"
        aria-live="polite"
      >
        {getStatusText()}
      </div>

      {/* Progress bar for training */}
      {trainingProgress && trainingProgress.status === 'progress' && (
        <div
          className="w-full bg-secondary rounded-full h-2"
          role="progressbar"
          aria-valuenow={trainingProgress.progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Training progress"
        >
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${trainingProgress.progress}%` }}
          />
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            You can close this page. We&apos;ll notify you when training is complete
            and your custom dice model is ready to use.
          </p>
        </CardContent>
      </Card>

      <Button
        variant="secondary-action"
        onClick={resetCurrentRegistration}
        aria-label="Register another dice"
      >
        Register Another Dice
      </Button>
    </div>
  );
}

/**
 * Main dice registration page
 */
export function DiceRegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    currentRegistration,
    deviceId,
    setDeviceId,
    fetchRegistrations,
    isEditMode,
    isLoading,
    startEditMode,
    exitEditMode
  } = useDiceRegistryStore();

  // Track whether mode selection has been completed (within 'select' step)
  const [modeSelected, setModeSelected] = useState(false);

  // Track if edit mode initialization has been attempted
  const [editInitialized, setEditInitialized] = useState(false);

  // Generate device ID if not present
  useEffect(() => {
    if (!deviceId) {
      const newDeviceId = crypto.randomUUID();
      console.info(`${SERVICE_TAG} Generated device ID`, { deviceId: newDeviceId });
      setDeviceId(newDeviceId);
    }
  }, [deviceId, setDeviceId]);

  // TICKET-088: Check for edit mode query params and initialize
  useEffect(() => {
    const isEditParam = searchParams.get('edit') === 'true';
    const diceTypeParam = searchParams.get('diceType') as DiceType | null;
    const deviceIdParam = searchParams.get('deviceId');

    if (isEditParam && diceTypeParam && deviceId && !editInitialized) {
      const editDeviceId = deviceIdParam || deviceId;
      console.info(`${SERVICE_TAG} Initializing edit mode`, { diceType: diceTypeParam, editDeviceId });
      setEditInitialized(true);
      startEditMode(editDeviceId, diceTypeParam);
    }
  }, [searchParams, deviceId, editInitialized, startEditMode]);

  // Cleanup edit mode when leaving the page
  useEffect(() => {
    return () => {
      if (isEditMode) {
        exitEditMode();
      }
    };
  }, [isEditMode, exitEditMode]);

  // Reset registration state when leaving the page (prevents stale capturing/training views on re-visit)
  useEffect(() => {
    return () => {
      const { isEditMode: editMode } = useDiceRegistryStore.getState();
      if (!editMode) {
        useDiceRegistryStore.getState().resetCurrentRegistration();
      }
    };
  }, []);

  // Fetch existing registrations on mount (only for non-edit mode)
  useEffect(() => {
    if (deviceId && !searchParams.get('edit')) {
      fetchRegistrations();
    }
  }, [deviceId, fetchRegistrations, searchParams]);

  // Reset mode selection when returning to select step
  useEffect(() => {
    if (currentRegistration.step === 'select') {
      setModeSelected(false);
    }
  }, [currentRegistration.step]);

  /**
   * Handle exiting edit mode (TICKET-088)
   */
  const handleExitEdit = () => {
    exitEditMode();
    navigate('/settings');
  };

  // Render based on current step
  const renderStep = () => {
    switch (currentRegistration.step) {
      case 'select':
        // TICKET-117: Skip mode selection when editing existing registration
        if (isEditMode) {
          return <DiceTypeSelector />;
        }
        // Show mode selection first, then dice type selection for new registrations
        if (!modeSelected) {
          return <TrainingIntensitySelector onContinue={() => setModeSelected(true)} />;
        }
        return <DiceTypeSelector onBack={() => setModeSelected(false)} />;
      case 'capturing':
        return <FaceCaptureView />;
      case 'reviewing':
        return <FaceReviewGrid isEditMode={isEditMode} onExitEdit={handleExitEdit} />;
      case 'training':
        return <TrainingStatus />;
      default:
        return <DiceTypeSelector />;
    }
  };

  // TICKET-104: In capturing step, use minimal padding and flex-1 for camera to fill space
  const isCapturing = currentRegistration.step === 'capturing';

  return (
    <div className={cn(
      'flex h-full flex-col bg-background',
      isCapturing ? 'p-0 overflow-hidden' : 'p-4 overflow-hidden'
    )}>
      {/* TICKET-088: Edit mode warning banner - positioned absolutely in capture mode */}
      {/* TICKET-108: Using Alert component for consistent styling */}
      {/* TICKET-115: Added dismissible with "don't show again" option */}
      {isEditMode && (
        <div className={cn(
          isCapturing
            ? 'absolute top-0 left-0 right-0 z-20 p-2'
            : 'w-full max-w-md mx-auto mb-4'
        )}>
          <Alert
            variant="warning"
            title={`Editing ${currentRegistration.diceName || currentRegistration.diceType?.toUpperCase()}`}
            dismissible
            storageKey="edit-mode-warning-dismissed"
          >
            Changes will require re-training your dice model (2-4 hours).
          </Alert>
        </div>
      )}
      <div className={cn(
        'w-full min-h-0',
        isCapturing
          ? 'flex-1 relative'
          : 'max-w-md mx-auto flex flex-col h-full'
      )}>
        {isLoading && isEditMode
          ? <LoadingFrog message="Loading registration..." />
          : renderStep()}
      </div>
    </div>
  );
}

export default DiceRegistrationPage;
